import { useCallback, useMemo, useRef } from 'react'

import {
  clamp,
  getMinTop,
  KEYBOARD_STEP,
  VIEWPORT_MARGIN,
} from './viewportBounds'

// Which edges each handle moves. The bottom-right corner uses the browser's own
// resize handle, so it is not listed; the top edge is the drag header.
export const RESIZE_HANDLES = {
  'top-left': { horizontal: 'left', vertical: 'top' },
  'top-right': { horizontal: 'right', vertical: 'top' },
  'bottom-left': { horizontal: 'left', vertical: 'bottom' },
  left: { horizontal: 'left' },
  right: { horizontal: 'right' },
  bottom: { vertical: 'bottom' },
}

export const HANDLE_NAMES = Object.keys(RESIZE_HANDLES)

// Arrow keys move the handle, so the arrow pointing away from the window grows it.
const KEY_DELTAS = {
  ArrowLeft: [-KEYBOARD_STEP, 0],
  ArrowRight: [KEYBOARD_STEP, 0],
  ArrowUp: [0, -KEYBOARD_STEP],
  ArrowDown: [0, KEYBOARD_STEP],
}

/**
 * Resizes the window from its corners and edges, keeping the opposite side in
 * place. The native CSS resize handle only exists at the bottom-right, so this
 * writes width/height to the element the same way it does.
 * @param {Object} windowRef - Ref to the window element
 * @param {{ x: number, y: number }} position - Current window position
 * @param {Function} setPosition - Position setter from useDraggable
 * @returns {Object} - Pointer and keyboard handler props for each handle in RESIZE_HANDLES
 */
function useResizeHandles(windowRef, position, setPosition) {
  const startRef = useRef(null)

  const snapshot = useCallback(() => ({
    x: position.x,
    y: position.y,
    width: windowRef.current.offsetWidth,
    height: windowRef.current.offsetHeight,
  }), [windowRef, position])

  const resize = useCallback((handle, start, dx, dy) => {
    const node = windowRef.current
    const style = window.getComputedStyle(node)
    const { horizontal, vertical } = RESIZE_HANDLES[handle]
    const right = start.x + start.width
    const bottom = start.y + start.height
    let { width, height } = start

    if (horizontal) {
      const minWidth = parseFloat(style.minWidth) || 0
      width = horizontal === 'left'
        ? clamp(start.width - dx, minWidth, right - VIEWPORT_MARGIN)
        : clamp(start.width + dx, minWidth, window.innerWidth - VIEWPORT_MARGIN - start.x)
      node.style.width = `${width}px`
    }
    if (vertical) {
      const minHeight = parseFloat(style.minHeight) || 0
      height = vertical === 'top'
        ? clamp(start.height - dy, minHeight, bottom - getMinTop())
        : clamp(start.height + dy, minHeight, window.innerHeight - VIEWPORT_MARGIN - start.y)
      node.style.height = `${height}px`
    }

    setPosition({
      x: horizontal === 'left' ? right - width : start.x,
      y: vertical === 'top' ? bottom - height : start.y,
    })
  }, [windowRef, setPosition])

  const handlePointerMove = useCallback((e) => {
    const start = startRef.current
    if (!start) {
      return
    }
    resize(start.handle, start, e.clientX - start.pointerX, e.clientY - start.pointerY)
  }, [resize])

  const handlePointerUp = useCallback((e) => {
    if (!startRef.current) {
      return
    }
    startRef.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
  }, [])

  return useMemo(() => Object.fromEntries(HANDLE_NAMES.map(handle => [handle, {
    onPointerDown: (e) => {
      if (e.button !== 0) {
        return
      }
      e.preventDefault()
      startRef.current = { ...snapshot(), handle, pointerX: e.clientX, pointerY: e.clientY }
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerUp,
    onKeyDown: (e) => {
      const delta = KEY_DELTAS[e.key]
      const { horizontal, vertical } = RESIZE_HANDLES[handle]
      // An edge handle only answers the arrows along its axis.
      if (!delta || (delta[0] && !horizontal) || (delta[1] && !vertical)) {
        return
      }
      e.preventDefault()
      resize(handle, snapshot(), delta[0], delta[1])
    },
  }])), [snapshot, resize, handlePointerMove, handlePointerUp])
}

export default useResizeHandles
